import { Link } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { api } from '../lib/api';
import { Card, CardHeader, Spinner, EmptyState, Badge } from '../components/ui';
import { fmtMonth } from '../lib/fmt';
import { summarizeVariance } from '../lib/fpaVarianceSummary';
import { AnswerBand, VarianceWaterfall, DataHealthChip } from '../components/FpaReadouts';

const hdr: React.CSSProperties = { fontSize: 13, fontWeight: 500 };
const STATUS_TONE: Record<string, 'green' | 'amber' | 'blue' | 'gray'> = {
  draft: 'gray', submitted: 'amber', approved: 'green', locked: 'blue', superseded: 'gray', restated: 'gray',
};

export default function AdminFpaHomePage() {
  const now = new Date();
  const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = `${lm.getFullYear()}-${String(lm.getMonth() + 1).padStart(2, '0')}`;

  const varQ = useQuery(() => api.fpa.variance({ from: lastMonth, to: lastMonth }), [lastMonth]);
  const plansQ = useQuery(() => api.fpa.plans.list());

  const baselineId = varQ.data?.baselines[0]?.id ?? null;
  const baselineName = varQ.data?.baselines[0]?.name ?? 'plan';
  const summary = varQ.data ? summarizeVariance(varQ.data, baselineId, lastMonth) : null;

  const plans = plansQ.data?.plans ?? [];
  const pending = plans.filter((p) => p.status === 'submitted');
  const recent = plans.slice(0, 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>FP&amp;A overview</div>
        <span style={{ marginLeft: 'auto' }}><DataHealthChip /></span>
      </div>

      {/* How are we vs plan — the answer first */}
      {varQ.isLoading ? <Card><Spinner /></Card>
        : summary ? <AnswerBand summary={summary} baselineName={baselineName} periodLabel={fmtMonth(lastMonth)} />
        : <Card><EmptyState message="No actuals/plan to compare yet — connect QBO and create a budget." /></Card>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12, alignItems: 'start' }}>
        {summary?.hasBaseline && <VarianceWaterfall summary={summary} baselineName={baselineName} />}

        {/* What needs you */}
        <Card>
          <CardHeader><div style={hdr}>Needs you</div></CardHeader>
          <div style={{ padding: '8px 16px 12px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
            {pending.length === 0
              ? <div style={{ color: 'var(--color-text-muted)' }}>No plans awaiting approval.</div>
              : pending.map((p) => (
                <Link key={p.id} to={`/admin/fpa/plans/${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit' }}>
                  <Badge variant="amber">submitted</Badge>
                  <span>{p.name} <span style={{ color: 'var(--color-text-hint)' }}>({p.kind})</span></span>
                  <span style={{ marginLeft: 'auto', color: 'var(--color-blue)' }}>Review →</span>
                </Link>
              ))}
            <div style={{ paddingTop: 4 }}><DataHealthChip /></div>
          </div>
        </Card>
      </div>

      {/* Plans at a glance */}
      <Card>
        <CardHeader>
          <div style={hdr}>Plans</div>
          <Link to="/admin/fpa/plans" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-blue)' }}>All plans →</Link>
        </CardHeader>
        {plansQ.isLoading ? <Spinner />
          : recent.length === 0 ? <EmptyState message="No plans yet — create your first budget." />
          : (
            <div style={{ padding: '4px 16px 12px', display: 'flex', flexDirection: 'column' }}>
              {recent.map((p) => (
                <Link key={p.id} to={`/admin/fpa/plans/${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '0.5px solid var(--color-border)', textDecoration: 'none', color: 'inherit' }}>
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-hint)' }}>{p.kind}</span>
                  <Badge variant={STATUS_TONE[p.status] ?? 'gray'}>{p.status}</Badge>
                  <span style={{ marginLeft: 'auto', color: 'var(--color-blue)', fontSize: 12 }}>Open →</span>
                </Link>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}
